import React, { useState } from 'react';
import {
  Box,
  Button,
  Collapse,
  Flex,
  FormControl,
  FormLabel,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Stack,
  Text,
  Textarea,
  useDisclosure,
} from '@chakra-ui/react';
import { useMutation } from '@tanstack/react-query';
import { CalendarBlank, ClockCounterClockwise, PencilSimple } from 'phosphor-react';
import { toast } from 'sonner';
import { useAuth } from '../../../../providers/auth';
import { IKanbanCard, useKanban } from '../../../../hooks/useKanban';
import { ILegalizacao, useLegalizacao } from '../../../../hooks/useLegalizacao';
import {
  CARD_SLA_KEY,
  dataInputParaIso,
  slaHistorico,
  SlaHistoricoEntry,
} from '../legalizacaoPlaybook';
import { SlaBadge } from '../../../../components/kanban/SlaBadge';

const fmtData = (iso: string | null): string => {
  if (!iso) return '—';
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '—' : d.toLocaleDateString('pt-BR');
};

const paraInput = (iso: string | null): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const p = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

// Conversão compartilhada com o campo de prazo da CRIAÇÃO (mora no playbook): duas
// implementações dariam cards com um dia de diferença conforme onde a data foi digitada.
const inputParaIso = dataInputParaIso;

interface SlaPrazoProps {
  card: IKanbanCard;
  legalizacao: ILegalizacao;
  onChanged: () => void;
  /** Recuo que alinha com o texto de um título com ícone. Na barra lateral do modal não há ícone: 0. */
  recuo?: string;
}

/**
 * Prazo (SLA) MANUAL do processo. A data vira `card.expira_em` (acende o semáforo
 * backend). Governança: 1ª definição livre; ALTERAR exige motivo + registra no
 * histórico (`estado_etapas._sla`: quem/quando/de→para/motivo). Card mostra "reajustado Nx".
 */
export const SlaPrazo: React.FC<SlaPrazoProps> = ({
  card,
  legalizacao,
  onChanged,
  recuo = '30px',
}) => {
  const { updateCard } = useKanban();
  const { atualizarLegalizacao } = useLegalizacao();
  const { user } = useAuth();
  const modal = useDisclosure();
  const hist = useDisclosure();

  // Otimista: card/legalizacao são snapshots do parent; reflete a mudança no drawer aberto.
  const [prazoLocal, setPrazoLocal] = useState<string | null>(null);
  const [histLocal, setHistLocal] = useState<SlaHistoricoEntry[] | null>(null);
  const [data, setData] = useState('');
  const [motivo, setMotivo] = useState('');

  const prazoAtual = prazoLocal ?? card.expira_em ?? null;
  const ehAlteracao = !!prazoAtual;
  const historico = histLocal ?? slaHistorico(legalizacao.estado_etapas);
  const reajustes = historico.filter(h => h.de != null).length; // exclui a 1ª definição

  const abrir = () => {
    setData(paraInput(prazoAtual));
    setMotivo('');
    modal.onOpen();
  };

  const salvar = useMutation({
    mutationFn: async () => {
      const novoIso = inputParaIso(data);
      const nome = (user as { nome?: string })?.nome ?? 'Operador';
      const entrada: SlaHistoricoEntry = {
        de: prazoAtual,
        para: novoIso,
        motivo: ehAlteracao ? motivo.trim() : '',
        por: nome,
        quando: new Date().toISOString(),
      };
      // 1) prazo → expira_em do card (fonte do semáforo backend). O motivo vai junto só
      //    para a linha da aba Atividade.
      await updateCard({
        id: card.id,
        expira_em: novoIso,
        motivo_prazo: entrada.motivo || undefined,
      });
      // 2) histórico → estado_etapas._sla (sem migration).
      await atualizarLegalizacao(legalizacao.id, {
        estado_etapas: {
          ...((legalizacao.estado_etapas as Record<string, unknown>) ?? {}),
          [CARD_SLA_KEY]: { historico: [...historico, entrada] },
        },
      });
      return { novoIso, entrada };
    },
    onSuccess: res => {
      setPrazoLocal(res.novoIso); // otimista no drawer aberto
      setHistLocal([...historico, res.entrada]);
      toast.success('Prazo salvo');
      onChanged();
      modal.onClose();
    },
    onError: () => toast.error('Não foi possível salvar o prazo.'),
  });

  const podeSalvar = !!data && (!ehAlteracao || !!motivo.trim());

  return (
    <Box>
      {/* `pl` alinha a data com o TEXTO do "Prazo (SLA)" acima, que começa depois do
          quadrado do ícone — sem isto ela encosta na borda do bloco e fica pendurada à
          esquerda do próprio rótulo. Mesmo recuo dos valores de "Nesta etapa"/"Total"
          (`RECUO_SECAO` no drawer); o número está repetido porque este componente não
          conhece o drawer, e um import só pra isto acoplaria os dois por um detalhe visual.
          ⚠️ Se o quadrado do ícone do SectionTitle mudar de tamanho, muda aqui também. */}
      <Flex align="center" justify="space-between" gap={2} wrap="wrap" pl={recuo}>
        {prazoAtual ? (
          <Flex align="center" gap={2}>
            {/* `gray.900` contra o `gray.600` do título — a cor é o que separa dado de
                rótulo agora que os dois têm o mesmo tamanho e peso. */}
            <Text fontSize="13px" fontWeight="700" color="gray.900">
              {fmtData(prazoAtual)}
            </Text>
            {card.sla && <SlaBadge sla={card.sla} rotuloCurto />}
          </Flex>
        ) : (
          <Text fontSize="11px" color="gray.400" fontStyle="italic">
            Sem prazo definido
          </Text>
        )}
        <Button
          size="xs"
          fontSize="13px"
          variant="ghost"
          colorScheme="purple"
          borderRadius="full"
          leftIcon={prazoAtual ? <PencilSimple size={13} /> : <CalendarBlank size={13} />}
          onClick={abrir}
        >
          {prazoAtual ? 'Alterar prazo' : 'Definir prazo'}
        </Button>
      </Flex>

      {/* Mesmo recuo da linha da data: esta nota pertence ao bloco "Prazo (SLA)" e ficaria
          desalinhada dela, encostando na borda enquanto a data começa 30px adiante. */}
      {reajustes > 0 && (
        <Box mt={1.5} pl={recuo}>
          <Button
            size="xs"
            fontSize="13px"
            variant="link"
            color="orange.500"
            fontWeight="500"
            leftIcon={<ClockCounterClockwise size={12} />}
            onClick={hist.onToggle}
          >
            prazo reajustado {reajustes}x
          </Button>
          <Collapse in={hist.isOpen}>
            <Stack spacing={1.5} mt={2} pl={2} borderLeftWidth="2px" borderColor="orange.100">
              {historico
                .filter(h => h.de != null)
                .slice()
                .reverse()
                .map(h => (
                  <Box key={h.quando} pl={2}>
                    <Text fontSize="11px" color="gray.500">
                      {fmtData(h.de)} → <b>{fmtData(h.para)}</b> · {fmtData(h.quando)} · {h.por}
                    </Text>
                    {h.motivo && (
                      <Text fontSize="11px" color="gray.400" fontStyle="italic">
                        &quot;{h.motivo}&quot;
                      </Text>
                    )}
                  </Box>
                ))}
            </Stack>
          </Collapse>
        </Box>
      )}

      <Modal isOpen={modal.isOpen} onClose={modal.onClose} isCentered>
        <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(4px)" />
        <ModalContent borderRadius="xl">
          <ModalHeader fontSize="18px" fontWeight="700">
            {ehAlteracao ? 'Alterar prazo (SLA)' : 'Definir prazo (SLA)'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={ehAlteracao ? 4 : 0}>
              <FormLabel fontSize="11px" fontWeight="700" color="gray.700">
                Data-limite do processo
              </FormLabel>
              <Input
                type="date"
                value={data}
                onChange={e => setData(e.target.value)}
                borderRadius="lg"
              />
              <Text fontSize="11px" color="gray.400" mt={1}>
                O semáforo (verde/amarelo/vermelho) é calculado em dias úteis a partir desta data.
              </Text>
            </FormControl>
            {ehAlteracao && (
              <FormControl isRequired>
                <FormLabel fontSize="11px" fontWeight="700" color="gray.700">
                  Motivo da alteração
                </FormLabel>
                <Textarea
                  value={motivo}
                  onChange={e => setMotivo(e.target.value)}
                  placeholder="ex.: cartório atrasou a análise"
                  rows={2}
                  borderRadius="lg"
                  fontSize="13px"
                />
                <Text fontSize="11px" color="gray.400" mt={1}>
                  Fica registrado no histórico (quem, quando, de→para) e visível no card.
                </Text>
              </FormControl>
            )}
          </ModalBody>
          <ModalFooter gap={3}>
            <Button variant="ghost" borderRadius="full" onClick={modal.onClose}>
              Cancelar
            </Button>
            <Button
              colorScheme="purple"
              borderRadius="full"
              px={5}
              onClick={() => salvar.mutate()}
              isLoading={salvar.isPending}
              isDisabled={!podeSalvar}
            >
              {ehAlteracao ? 'Salvar alteração' : 'Definir prazo'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default SlaPrazo;
